import type { ComponentChildren } from "preact";
import type { BetResult, RankedRow } from "../domain/bet-outcome.js";
import { fmt } from "../domain/numbers.js";
import { formatFinishTime } from "../../shared/race-prediction.js";
import { podiumSteps } from "../domain/bet-outcome.js";

/** 開獎畫面：頒獎台、其他猜對的人、個人結果與彩帶。兩季的競猜共用。 */

export interface RevealState {
  /** 這個分頁正在顯示才放彩帶，所有分頁都常駐 DOM。 */
  active: boolean;
  seen: string[];
  onSeen(id: string): void;
}

export const formatKg = (diff: number) => `${fmt(diff)} kg`;

/** 彩帶的位置與顏色固定寫好，每次都一樣，測試也不用對付亂數。 */
const CONFETTI = Array.from({ length: 28 }, (_, index) => ({
  left: `${(index * 37 + 5) % 100}%`,
  delay: `${(index % 7) * 90}ms`,
  color: ["var(--pink)", "var(--yellow)", "var(--purple)", "var(--lime)"][index % 4]
}));

/**
 * 看完才記成看過：動畫跑完才呼叫 `onSeen`。系統要求減少動態效果時 CSS 不顯示，
 * 動畫不會跑，畫面上也就沒有東西會動。
 */
function Confetti({ id, reveal }: { id: string; reveal: RevealState }) {
  if (!reveal.active || reveal.seen.includes(id)) return null;
  return (
    <div class="confetti" aria-hidden="true">
      {CONFETTI.map((piece, index) => (
        <i
          key={index}
          style={{ left: piece.left, animationDelay: piece.delay, background: piece.color }}
          onAnimationEnd={index === CONFETTI.length - 1 ? () => reveal.onSeen(id) : undefined}
        />
      ))}
    </div>
  );
}

function diffText(diff: number | null, unit: (diff: number) => string): string | null {
  if (diff === null) return null;
  return diff === 0 ? "完全猜中" : `只差 ${unit(diff)}`;
}

/** 雙料預言家是稱號不是名次，所以用緞帶，不站領獎台。 */
export function DoubleRibbon({ id, names, reveal, revealId }: {
  id: string;
  names: string[];
  reveal: RevealState;
  revealId: string;
}) {
  return (
    <section id={id} class="award-ribbon" aria-label="雙料預言家">
      <Confetti id={revealId} reveal={reveal} />
      <span>雙料預言家</span>
      <strong>{names.join("、")}</strong>
      <small>兩題都猜對了</small>
    </section>
  );
}

function PodiumStep({ place, rows, unit, mascot }: {
  place: 1 | 2 | 3;
  rows: RankedRow[];
  unit: (diff: number) => string;
  mascot?: boolean;
}) {
  const diff = rows[0]?.diff ?? null;
  const detail = place === 1
    ? diffText(diff, unit)
    : diff === null ? null : `差 ${unit(diff)}`;
  return (
    <div class={`podium-step place-${place}`}>
      <div class="podium-people">
        {mascot && <img class="award-mascot" src="/mascot-supporter-mini.png" alt="" width="64" height="64" />}
        {rows.map(row => <strong key={row.nickname}>{row.nickname}</strong>)}
        {detail && <small>{detail}</small>}
      </div>
      <div class="podium-block" aria-label={`第 ${place} 名`}><b>{place}</b></div>
    </div>
  );
}

/**
 * 一題的開獎：前三名站上領獎台，第四名以後排在台下，一樣看得到。
 *
 * 排法是奧運的 2、1、3，第一名在中間最高。沒有人站的那一階也留著，台子才像台子。
 *
 * `answerWasYes` 決定稱讚的方向。結果是「沒做到」時，贏家押的是「做不到」，這時候
 * 稱讚對方的眼光，不是恭喜失敗。
 */
export function QuestionAwards({ id, label, ranking, answerWasYes, unit, fact, emptyText, reveal, revealId }: {
  id: string;
  label: string;
  ranking: RankedRow[];
  answerWasYes: boolean;
  unit: (diff: number) => string;
  fact: ComponentChildren;
  emptyText: string;
  reveal: RevealState;
  revealId: string;
}) {
  const { first, second, third, others } = podiumSteps(ranking);
  const revealing = reveal.active && !reveal.seen.includes(revealId);
  return (
    <div id={id} class="bet-board">
      {first.length === 0 ? (
        <>
          <p class="bet-camp-line award-fact">{fact}</p>
          <small>{emptyText}</small>
        </>
      ) : (
        <section class={`award${revealing ? " is-revealing" : ""}`} aria-label={`${label}的預言家`}>
          <Confetti id={revealId} reveal={reveal} />
          <p class="award-title">{label}的預言家</p>
          <p class="award-note">{answerWasYes ? "一開始就相信了" : "一開始就看穿了"}</p>
          <div class="podium">
            <PodiumStep place={2} rows={second} unit={unit} />
            <PodiumStep place={1} rows={first} unit={unit} mascot />
            <PodiumStep place={3} rows={third} unit={unit} />
          </div>
          <p class="award-fact">{fact}</p>
        </section>
      )}
      {others.map(row => (
        <div key={row.nickname} class="bet-row hit">
          <b>#{row.rank} {row.nickname}</b>
          <span>{row.diff === null ? "猜對了" : `差 ${unit(row.diff)}`}</span>
        </div>
      ))}
    </div>
  );
}

function resultLine(label: string, result: BetResult, unit: (diff: number) => string): string {
  if (result.status === "void") return `${label}賽事取消，這一題作廢。`;
  if (result.status === "pending") return `${label}還沒開獎。`;
  if (result.status === "wrong") return `${label}沒猜中，下次再來。`;
  const detail = diffText(result.diff, unit);
  return `你猜對了！${label}第 ${result.rank} 名${detail ? `，${detail}` : ""}。`;
}

/** 開獎後告訴猜的人結果，不用自己去名單比對。 */
export function MyBetResult({ id, rows, double }: {
  id: string;
  rows: { label: string; result: BetResult; unit: (diff: number) => string }[];
  double?: boolean;
}) {
  const won = rows.some(row => row.result.status === "correct");
  return (
    <div id={id} class={`my-result${won ? " won" : ""}`}>
      {double && <strong>雙料預言家！兩題都猜對了。</strong>}
      {rows.map(row => <p key={row.label}>{resultLine(row.label, row.result, row.unit)}</p>)}
    </div>
  );
}

export const formatFinish = (diff: number) => formatFinishTime(diff);
